import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { X, Users, TrendingUp, Pencil, Trash2 } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { taskService } from '@/services/taskService'
import { useToast } from '@/contexts/ToastContext'
import { Task, TaskStatus, TaskPriority } from '@/types/task'

interface GroupTaskViewProps {
  groupId: string
  isOpen: boolean
  onClose: () => void
  onSelectTask?: (task: Task) => void
  onGroupUpdated?: () => void
}

const PRIORITIES: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
]

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case 'completed':
      return 'text-green-600 bg-green-100'
    case 'in_progress':
      return 'text-accent-700 bg-accent-100'
    case 'under_review':
      return 'text-yellow-600 bg-yellow-100'
    case 'pending':
      return 'text-gray-600 bg-gray-100'
    case 'cancelled':
      return 'text-red-600 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

const GroupTaskView: React.FC<GroupTaskViewProps> = ({
  groupId,
  isOpen,
  onClose,
  onSelectTask,
  onGroupUpdated
}) => {
  const { showToast } = useToast()
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(false)

  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [editPriority, setEditPriority] = useState<TaskPriority>('medium')
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadGroupTasks = () => {
    let cancelled = false
    setLoading(true)

    taskService
      .getGroupTasks(groupId)
      .then((data) => {
        if (!cancelled) setTasks(data)
      })
      .catch((error) => {
        if (!cancelled) {
          showToast({
            type: 'error',
            message: error instanceof Error ? error.message : 'Failed to load group tasks'
          })
          onClose()
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }

  useEffect(() => {
    if (!isOpen) return
    return loadGroupTasks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, isOpen])

  const openEditForm = () => {
    const first = tasks[0]
    if (!first) return
    setEditTitle(first.title)
    setEditDescription(first.description)
    setEditDueDate(first.dueDate ? first.dueDate.slice(0, 10) : '')
    setEditPriority(first.priority)
    setIsEditing(true)
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmittingEdit(true)

    try {
      const { updated, skipped } = await taskService.updateGroupTask(groupId, {
        title: editTitle,
        description: editDescription,
        dueDate: editDueDate || undefined,
        priority: editPriority
      })

      showToast({
        type: 'success',
        message:
          skipped.length > 0
            ? `Updated ${updated.length} task(s), skipped ${skipped.length} (already completed or cancelled)`
            : `Updated ${updated.length} task(s)`
      })

      setIsEditing(false)
      onGroupUpdated?.()
      loadGroupTasks()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update group'
      })
    } finally {
      setIsSubmittingEdit(false)
    }
  }

  const handleDeleteGroup = async () => {
    if (!window.confirm('Delete this group assignment? Mentees who already completed or submitted work are protected and will keep their task.')) {
      return
    }

    setIsDeleting(true)

    try {
      const { deletedCount, preserved } = await taskService.deleteGroupTask(groupId)

      showToast({
        type: 'success',
        message:
          preserved.length > 0
            ? `Deleted ${deletedCount} task(s), preserved ${preserved.length} (already completed or submitted)`
            : `Deleted ${deletedCount} task(s)`
      })

      onGroupUpdated?.()
      loadGroupTasks()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete group'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Group Task Assignment
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
              </div>
            ) : isEditing ? (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <p className="text-sm text-gray-500">
                  Changes apply to every mentee whose task isn't already completed or cancelled.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    minLength={3}
                    maxLength={100}
                    required
                    disabled={isSubmittingEdit}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                    rows={4}
                    minLength={10}
                    maxLength={2000}
                    required
                    disabled={isSubmittingEdit}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <input
                      type="date"
                      value={editDueDate}
                      onChange={(e) => setEditDueDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isSubmittingEdit}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={editPriority}
                      onChange={(e) => setEditPriority(e.target.value as TaskPriority)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isSubmittingEdit}
                    >
                      {PRIORITIES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 justify-end pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setIsEditing(false)}
                    disabled={isSubmittingEdit}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" isLoading={isSubmittingEdit}>
                    Save Changes
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 mb-2">
                  {tasks.length} {tasks.length === 1 ? 'mentee' : 'mentees'} in this group assignment
                </p>
                {tasks.map((task) => (
                  <Card key={task.id}>
                    <CardBody>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {task.mentee && (
                              <Link to={`/users/${task.mentee.id}`} className="hover:underline">
                                {task.mentee.firstName} {task.mentee.lastName}
                              </Link>
                            )}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}
                            >
                              {task.status.replace('_', ' ')}
                            </span>
                            <span className="flex items-center gap-1">
                              <TrendingUp className="w-3.5 h-3.5" />
                              {task.progressPercentage}%
                            </span>
                          </div>
                        </div>
                        {onSelectTask && (
                          <Button variant="outline" size="sm" onClick={() => onSelectTask(task)}>
                            View
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {!isEditing && (
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              {!loading && tasks.length > 0 && (
                <>
                  <Button
                    variant="danger"
                    onClick={handleDeleteGroup}
                    isLoading={isDeleting}
                    disabled={isDeleting}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Group
                  </Button>
                  <Button variant="outline" onClick={openEditForm} disabled={isDeleting}>
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit Group
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GroupTaskView
